import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@babackapp/ui/components/dialog";
import useTranslation from "@babackapp/translations/use-translation";
import { useCallback, useState } from "react";
import { useCarriersContext } from "../../use-carriers-context";
import {
  CarrierDto,
  UpdateCarrierDto,
  UpdateSwissPostCarrierPreferencesDto,
} from "@babackapp/private-api-client/types";
import { Input } from "@babackapp/ui/components/input";
import { useViewerHasAtLeastAdminRights } from "@/lib/rights/use-viewer-has-right";
import { CarrierTransportForm } from "./carrier-transport-form";
import { FedexPlanSelect } from "./fedex-plan-select";
import { FedexRatesPanel } from "../components/fedex-rates-button";
import { valueFromPreferences } from "../fedex-plans";

export const EditCarrierTransportDialog = ({
  isOpen,
  setIsModalOpen,
  selectedCarrier,
  onClose,
}: {
  isOpen: boolean;
  setIsModalOpen: (open: boolean) => void;
  selectedCarrier: CarrierDto;
  onClose: () => void;
}) => {
  const t = useTranslation();
  const { editOneCarrierTransport } = useCarriersContext();
  const { addOneUpsCarrierTransport } = useCarriersContext();
  const hasAdminRights = useViewerHasAtLeastAdminRights();
  // Own the selected product so FedexPlanSelect and the rate panel share it.
  const [serviceValue, setServiceValue] = useState(() =>
    valueFromPreferences(selectedCarrier.preferences),
  );
  const onSubmitEditCarrierTransport = useCallback((data: UpdateCarrierDto) => {
    editOneCarrierTransport({ carrier: data, carrierId: selectedCarrier.id });
    onClose();
  }, []);

  const onSubmitUps = useCallback(
    (data: { accountNumber: string; carrierType: string }) => {
      addOneUpsCarrierTransport({
        accountNumber: data.accountNumber,
        carrierType: data.carrierType,
        carrierId: selectedCarrier.id,
      });
    },
    [],
  );
  return (
    <Dialog open={isOpen} onOpenChange={setIsModalOpen}>
      <DialogContent className="pt-10">
        <DialogHeader>
          <DialogTitle> {t("carrier.label")}</DialogTitle>
          <DialogDescription>{t("carrier.subLabel")}</DialogDescription>
        </DialogHeader>
        {selectedCarrier.code === "fedex" && (
          <section className="space-y-3 rounded-md border border-gray-200 p-4">
            <div className="space-y-1">
              <h3 className="text-sm font-semibold text-gray-900">
                {t("carrier.fedexCsp.planSectionTitle")}
              </h3>
              <p className="text-xs text-gray-500">
                {t("carrier.fedexCsp.planSectionDescription")}
              </p>
            </div>
            <div className="space-y-1.5">
              <label className="block text-sm font-medium text-gray-700">
                {t("carrier.fedexCsp.accountNumber")}
              </label>
              <Input
                value={selectedCarrier.preferences?.accountNumber ?? ""}
                disabled
                readOnly
              />
            </div>
            <FedexPlanSelect
              carrierId={selectedCarrier.id}
              preferences={selectedCarrier.preferences}
              value={serviceValue}
              onValueChange={setServiceValue}
            />
            {hasAdminRights && (
              <FedexRatesPanel
                carrier={selectedCarrier}
                selectedServiceValue={serviceValue}
              />
            )}
          </section>
        )}
        {selectedCarrier.code !== "fedex" && (
          <CarrierTransportForm
            defaultValues={{
              code: selectedCarrier.code,
              credentials: {
                accountId: selectedCarrier.preferences.accountId || undefined,
                webSiteId: selectedCarrier.preferences?.webSiteId || undefined,
                accountNumber:
                  selectedCarrier.preferences?.accountNumber || undefined,
                subAccount:
                  selectedCarrier.preferences?.subAccount || undefined,
                SLS: selectedCarrier.preferences?.SLS || undefined,
                eori: selectedCarrier.preferences?.eori || undefined,
                customerCountryCode:
                  selectedCarrier.preferences?.customerCountryCode || undefined,
                customerCenterNumber:
                  selectedCarrier.preferences?.customerCenterNumber ||
                  undefined,
                customerNumber:
                  selectedCarrier.preferences?.customerNumber || undefined,
                userid: selectedCarrier.preferences?.userid || undefined,
                shipperAccountNumber:
                  selectedCarrier.preferences?.shipperAccountNumber ||
                  undefined,
                brand: selectedCarrier.preferences?.brand || undefined,
                login: selectedCarrier.preferences?.login || undefined,
                password: selectedCarrier.preferences?.password || undefined,
                securityKey:
                  selectedCarrier.preferences?.securityKey || undefined,
                livRel: selectedCarrier.preferences?.livRel || undefined,
                // DHL Parcel DE fields (OAuth + optional)
                clientId: selectedCarrier.preferences?.clientId || undefined,
                clientSecret:
                  selectedCarrier.preferences?.clientSecret || undefined,
                username: selectedCarrier.preferences?.username || undefined,
                receiverId:
                  selectedCarrier.preferences?.receiverId || undefined,
                // DPD CH fields
                invoicingNumber:
                  selectedCarrier.preferences?.invoicingNumber || undefined,
                // Swiss Post fields (clientId/clientSecret above)
                frankingLicense:
                  selectedCarrier.preferences?.frankingLicense || undefined,
                service: (selectedCarrier.preferences?.service ||
                  undefined) as UpdateSwissPostCarrierPreferencesDto["service"],
                domicilePostOffice:
                  selectedCarrier.preferences?.domicilePostOffice || undefined,
              },
            }}
            onSubmit={onSubmitEditCarrierTransport}
            onSubmitUps={onSubmitUps}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};
